import { ArrowLeft, Columns2, Moon, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, type RouteObject } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import {
  useRightPanel,
  type RightPanelConfig,
  type RightPanelItem,
} from '@/components/layout/workspace/rightPanelContext';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  Badge,
  Button,
  EmptyState,
  EntityText,
  IconButton,
  PropertyList,
  PropertyRow,
  RelativeTime,
  TimeInterval,
  ToastProvider,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { DutyView } from './DutyView';
import { useDutyData, type DutyData } from './useDutyData';
import { HOME, NOW, PEOPLE, ROWS, resolveDuty } from './model';
export function DutyPatterns() {
  return (
    <ToastProvider>
      <WorkspaceProvider homePath={HOME} resolve={resolveDuty} storageKey="kaname.duty.workspace.v1">
        <ObserverProvider home="core" storageKey="kaname.duty.observer.v1">
          <SampleShell />
        </ObserverProvider>
      </WorkspaceProvider>
    </ToastProvider>
  );
}
function SampleShell() {
  const data = useDutyData(),
    ws = useWorkspace(),
    { mode, toggle } = useTheme(),
    [expanded, setExpanded] = useState(false);
  const routes = useMemo<RouteObject[]>(
    () => [
      { path: '/event/:id', element: <EventPage data={data} /> },
      { path: '/person/:id', element: <PersonPage /> },
      { path: '*', element: <Examples data={data} /> },
    ],
    [data],
  );
  const panel = useMemo<RightPanelConfig>(
    () => ({
      storageKey: 'kaname.duty.panels.v1',
      render: (item) => <Details data={data} item={item} />,
      title: (item) =>
        item.path.includes('/event/')
          ? (data.events.find((e) => e.id === item.id)?.title ?? 'Событие недоступно')
          : item.title,
    }),
    [data],
  );
  return (
    <div className="flex h-screen flex-col bg-frame text-fg">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design#duty-patterns"
          className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted hover:text-brand-fg"
        >
          <ArrowLeft className="size-4" />
          Галерея
        </Link>
        <div className="order-first w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
          <h1 className="type-subheading">Сетка дежурств</h1>
          <p className="text-xs text-fg-muted">Группы · слои · карточка события</p>
        </div>
        <Badge>Образец</Badge>
        <Button size="sm" variant="secondary" onClick={() => ws.openBeside(HOME + '/event/duty-0-2')}>
          <Columns2 className="size-4" />
          Второе окно
        </Button>
        <IconButton
          variant="secondary"
          aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
          onClick={toggle}
        >
          {mode === 'dark' ? <Sun /> : <Moon />}
        </IconButton>
      </header>
      <div data-shell="" className="relative flex min-h-0 flex-1 gap-3 p-3">
        <MainMenu
          expanded={expanded}
          onToggleExpand={() => setExpanded((value) => !value)}
          homeActive
          onHome={() => ws.go(HOME)}
          activeId={null}
          sections={[]}
          tools={[]}
          onSelect={() => ws.go(HOME)}
        />
        <WorkspaceCanvas routes={routes} rightPanel={panel} />
      </div>
    </div>
  );
}
function Examples({ data }: { data: DutyData }) {
  const panel = useRightPanel();
  return (
    <main className="flex min-w-0 flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 3</p>
        <h2 tabIndex={-1} data-panel-default-focus="" className="type-title">
          Дежурства
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Нажми на смену, чтобы увидеть подробности и действия. Расписания можно свернуть.
        </p>
      </header>
      <DutyView
        data={data}
        onDetails={(event, trigger) =>
          panel.open(
            { id: event.id, path: HOME + '/event/' + event.id, title: event.title, eyebrow: 'Событие' },
            trigger,
          )
        }
        onPerson={(id, trigger) =>
          panel.open(
            {
              id,
              path: HOME + '/person/' + id,
              title: PEOPLE.find((p) => p.id === id)?.name ?? 'Участник',
              eyebrow: 'Человек',
            },
            trigger,
          )
        }
      />
    </main>
  );
}
function Details({ data, item }: { data: DutyData; item: RightPanelItem }) {
  return item.path.includes('/person/') ? (
    <PersonContents id={item.id} />
  ) : (
    <EventContents id={item.id} data={data} />
  );
}
function PersonContents({ id }: { id: string }) {
  const person = PEOPLE.find((p) => p.id === id);
  return person ? (
    <PropertyList>
      <PropertyRow label="Имя">{person.name}</PropertyRow>
      <PropertyRow label="Данные">Вымышленные, для образца</PropertyRow>
    </PropertyList>
  ) : (
    <EmptyState title="Участник не найден" />
  );
}
function EventContents({ id, data }: { id: string; data: DutyData }) {
  const event = data.events.find((e) => e.id === id),
    panel = useRightPanel();
  if (!event)
    return (
      <EmptyState
        title="Событие не найдено"
        description="Возможно, оно удалено из образца. Отмена доступна в уведомлении."
      />
    );
  return (
    <PropertyList>
      <PropertyRow label="Название">{event.title}</PropertyRow>
      <PropertyRow label="Статус">
        <Badge variant={event.tentative ? 'warn' : 'info'} solid>
          {event.tentative ? 'Предварительно' : 'Подтверждено'}
        </Badge>
      </PropertyRow>
      <PropertyRow label="Слой">{ROWS.find((r) => r.id === event.rowId)?.title}</PropertyRow>
      <PropertyRow label="Участник">
        <EntityText
          name={PEOPLE.find((p) => p.id === event.personId)?.name ?? 'Участник'}
          onClick={(eventClick) =>
            panel.open(
              {
                id: event.personId,
                path: HOME + '/person/' + event.personId,
                title: PEOPLE.find((p) => p.id === event.personId)?.name ?? 'Участник',
              },
              eventClick.currentTarget,
            )
          }
        />
      </PropertyRow>
      <PropertyRow label="Интервал">
        <TimeInterval start={event.start} end={event.end} zoneLabel="Москва" />
      </PropertyRow>
      <PropertyRow label="Добавлено">
        <RelativeTime at={event.createdAt} now={NOW} />
      </PropertyRow>
    </PropertyList>
  );
}
function EventPage({ data }: { data: DutyData }) {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <h2 className="mb-6 type-title">{data.events.find((e) => e.id === id)?.title ?? 'Событие'}</h2>
      <EventContents id={id} data={data} />
    </article>
  );
}
function PersonPage() {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <h2 className="mb-6 type-title">{PEOPLE.find((p) => p.id === id)?.name ?? 'Участник'}</h2>
      <PersonContents id={id} />
    </article>
  );
}
